type NativeEvent = Event;

import {Component, Element, Event, EventEmitter, h, Method, Prop, State} from '@stencil/core';
import {generatedInputNameAndId} from '@/utils';

@Component({
    tag: 'joy-dropzone',
    styleUrl: 'dropzone.scss',
    scoped: true,
})
export class Dropzone {
    private input!: HTMLInputElement;

    @Element() host!: HTMLJoyDropzoneElement;

    /** set a unique id for the input file. Required for the drag and drop ! */
    @Prop({reflect: true}) idDropzone!: string;
    /** @deprecated Makes the dropzone disabled or not */
    @Prop({reflect: true}) disabled = false;
    /** Makes the dropzone invalid or not */
    @Prop({reflect: true}) invalid = false;
    /** Add accepted file formats */
    @Prop({reflect: true}) accept = '';
    /** set MaxSize (Megabytes), default to 32 MB */
    @Prop({reflect: true}) maxSize = 32;
    /** Add description Text */
    @Prop({reflect: true}) descriptionText = 'Drop your file here or';
    /** Add description Text */
    @Prop({reflect: true}) descriptionStrongText = 'click here';
    /** Add CTA Upload Text */
    @Prop({reflect: true}) buttonText = 'Upload';
    /** Add CTA Cancellation Text */
    @Prop({reflect: true}) buttonCancelText: string = 'Cancel';
    /** @deprecated Add Uploading Text */
    @Prop({reflect: true}) uploadingText: string = 'Uploading... hang tight.';
    /** Add legend/specification text */
    @Prop({reflect: true}) legend?: string | null;
    /** Enable multiple files upload */
    @Prop({reflect: true}) multiple = false;
    /** Add custom error message when uploaded file format is not accepted */
    @Prop({reflect: true}) errorTextFormat = '';
    /** Add custom error message when uploaded file size is bigger than maxSize */
    @Prop({reflect: true}) errorTextMaxSize = '';
    /** @deprecated Make the dropzone in progress state or not */
    @Prop({reflect: true}) loading = false;
    /** Override default aria-label for the "trash" icon associated with each selected file. */
    @Prop() deleteLabel: string = 'Delete this file';

    @State() dragover = false;
    @State() files: File[] = [];
    @State() fileInvalid = false;

    /** @deprecated */
    @Event({eventName: 'joy-dropzone-cancel-upload-file'}) joyDropzoneCancelUploadFile!: EventEmitter<File[] | null>;
    /** @deprecated */
    @Event({eventName: 'joy-dropzone-invalidate-file'}) joyDropzoneInvalidateFile!: EventEmitter<File[] | null>;
    @Event({eventName: 'joy-dropzone-dropped-files'}) joyDropzoneDroppedFiles!: EventEmitter<File[] | null>;
    @Event({eventName: 'joy-dropzone-remove-file'}) joyDropzoneRemoveFile!: EventEmitter<File | null>;
    /** @deprecated */
    @Event({eventName: 'joy-dropzone-buttons-click'}) joyDropzoneButtonsClick!: EventEmitter<null>;

    /**
     * set In progress state
     * @param {boolean} loading
     * @return {Promise}
     */
    @Method()
    async isLoading(loading: boolean): Promise<void> {
        this.loading = loading;
    }

    /**
     * reset the dropzone to its original state
     * @return {Promise}
     */
    @Method()
    async resetDropzone(): Promise<void> {
        this.files = [];
        this.invalid = false;
        this.fileInvalid = false;
        this.input.value = '';
    }

    private errorMessages: string[] = [];

    constructor() {
        this.dragoverHandler = this.dragoverHandler.bind(this);
        this.dragLeaveHandler = this.dragLeaveHandler.bind(this);
        this.dropHandler = this.dropHandler.bind(this);
    }

    connectedCallback() {
        window.addEventListener('dragover', this.dragoverHandler);
        window.addEventListener('dragleave', this.dragLeaveHandler);
        window.addEventListener('drop', this.dropHandler);
    }

    disconnectedCallback() {
        window.removeEventListener('dragover', this.dragoverHandler);
        window.removeEventListener('dragleave', this.dragLeaveHandler);
        window.removeEventListener('drop', this.dropHandler);
    }

    render() {
        return (
            <div
                class={{
                    'joy-dropzone': true,
                    'joy-dropzone--focusing': this.dragover,
                    'joy-dropzone--disabled': this.disabled,
                    'joy-dropzone--invalid': this.invalid || this.fileInvalid,
                    'joy-dropzone--valid': !this.invalid && !this.disabled && !!this.files.length,
                    [this.idDropzone || generatedInputNameAndId(this.host)]: true,
                }}
            >
                <div class="joy-dropzone__area">
                    <input
                        id={this.idDropzone || generatedInputNameAndId(this.host)}
                        ref={(el) => (this.input = el as HTMLInputElement)}
                        name={this.idDropzone}
                        type="file"
                        accept={this.accept}
                        onChange={this.updateFile}
                        multiple={this.multiple}
                        disabled={this.disabled}
                        tabindex="-1"
                    />
                    {!this.loading && (
                        <label
                            htmlFor={this.idDropzone || generatedInputNameAndId(this.host)}
                            class={{
                                'joy-dropzone-label': true,
                                'joy-dropzone--dragover': this.dragover,
                                'joy-dropzone--error': this.fileInvalid,
                            }}
                            onClick={(e) => e.preventDefault()}
                        >
                            {this.filesTemplate()}
                            {this.emptyFilesTemplate()}
                        </label>
                    )}
                    {this.loading && (
                        <div>
                            <div class="joy-dropzone__loader">
                                <joy-spinner color="teal"></joy-spinner>
                            </div>
                            <p class="joy-dropzone__description">{this.uploadingText}</p>
                            <joy-button type="button" onClick={this.cancelUpload} variant="ghost">
                                {this.buttonCancelText}
                            </joy-button>
                        </div>
                    )}
                </div>
                {this.legend && <div class="joy-dropzone__info">{this.legend}</div>}
                {this.errorMessages.map((errorMessage) => (
                    <div class="joy-dropzone__info">
                        <joy-form-error no-html-error-text={errorMessage}></joy-form-error>
                    </div>
                ))}
            </div>
        );
    }

    private emptyFilesTemplate() {
        if (!this.multiple && this.files.length) {
            return '';
        }

        return (
            <joy-wrapper
                class="joy-dropzone__file___wrapper"
                column-gap="4"
                row-gap="4"
                justify="flex-start"
                align="center"
                no-margin={!this.files.length}
            >
                <div class="joy-dropzone__plus-icon">
                    <joy-icon aria-hidden="true" name="add"></joy-icon>
                </div>
                <p class="joy-dropzone__description">
                    {this.descriptionText} <strong onClick={this.handleClickCTA}>{this.descriptionStrongText}</strong>
                </p>
                {this.uploadButton()}
            </joy-wrapper>
        );
    }

    private uploadButton() {
        return (
            <joy-button class="joy-dropzone__cta" size="xsmall" variant="primary" disabled={this.disabled} onClick={this.handleClickCTA}>
                {this.buttonText}
            </joy-button>
        );
    }

    private filesTemplate() {
        return this.files.map((file: File) => (
            <joy-wrapper
                class="joy-dropzone__file___wrapper joy-dropzone__file--is-file"
                column-gap="6"
                row-gap="4"
                justify="flex-start"
                align="center"
            >
                <div class="joy-dropzone__file-icon">
                    <joy-icon aria-hidden="true" size="xsmall" name="file-check" color="grey" />
                </div>
                <div class="joy-dropzone__item">
                    <span>{file.name}</span>
                </div>
                <joy-wrapper class="joy-dropzone__cta" no-margin>
                    <joy-icon
                        title={this.deleteLabel}
                        aria-label={this.deleteLabel}
                        name="trash"
                        color="grey"
                        onClick={(event) => this.removeFile(event, file)}
                    ></joy-icon>
                    {!this.multiple && this.uploadButton()}
                </joy-wrapper>
            </joy-wrapper>
        ));
    }

    private dragoverHandler(e: NativeEvent) {
        e.stopPropagation();
        e.preventDefault();
        this.dragover = this.checkEventTarget(e);
    }

    private dragLeaveHandler(e: NativeEvent) {
        e.stopPropagation();
        e.preventDefault();
        this.dragover = false;
    }

    private cancelUpload() {
        this.joyDropzoneCancelUploadFile.emit();
        this.loading = false;
    }

    private checkEventTarget(e: NativeEvent) {
        // TODO : Need to refacto this condition, if no id-dropzone is set, we can't drag and drop
        return !!(e.target as HTMLElement).closest(`.${this.idDropzone}`);
    }

    private checkFileAndTriggerUpdate(files: File[]) {
        this.files = [];
        const validations = Array.from(files).map((file) => this.isFileValid(file));
        validations.forEach((validation, index) => {
            if (validation.valid) {
                if (this.multiple) {
                    this.files = [...this.files, files[index]];
                } else {
                    this.files = [files[index]];
                }
                this.clearErrorStatus();
                this.joyDropzoneDroppedFiles.emit(files);
            } else {
                this.fileInvalid = true;
                this.errorMessages.push(validation.message);
                this.joyDropzoneInvalidateFile.emit(files);
            }
        });
    }

    private clearErrorStatus() {
        if (this.errorMessages.length) {
            this.errorMessages = [];
        }
        this.fileInvalid = false;
    }

    private isFileValid(file: File) {
        return this.validationChecks().reduce(
            (acc, curr) => ({
                valid: acc.valid && curr.check(file),
                message: curr.check(file) ? acc.message : curr.message,
            }),
            {valid: true, message: ''},
        );
    }

    private validationChecks() {
        return [
            {
                check: (file: File) => (this.accept ? this.accept.includes(file.type) : true),
                message: this.errorTextFormat || 'The file format is invalid',
            },
            {
                check: (file: File) => file.size < this.maxSize * 1024 * 1024,
                message: this.errorTextMaxSize || `Your file exceeds the ${this.maxSize} MB size limit`,
            },
        ];
    }

    private dropHandler(e: DragEvent) {
        if (!this.checkEventTarget(e)) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        this.dragover = false;
        const fileList = e.dataTransfer?.files;
        if (fileList) {
            const files = this.convertFileListToFilesArray(fileList);
            files && this.checkFileAndTriggerUpdate(files);
        }
    }

    private updateFile = (e: NativeEvent) => {
        const target = e.target as HTMLInputElement & EventTarget;
        const fileList = target?.files;
        if (fileList) {
            const files = this.convertFileListToFilesArray(fileList);
            files && this.checkFileAndTriggerUpdate(files);
        }
    };

    private convertFileListToFilesArray(fileList: FileList) {
        const files = [];
        let i = 0;
        for (i; i < fileList.length; i++) {
            if (fileList.item(i)) {
                files.push(fileList.item(i) as File);
            }
        }
        return files;
    }

    private handleClickCTA = (e: NativeEvent) => {
        e.preventDefault();

        if (!this.disabled) {
            this.input.click();
            this.joyDropzoneButtonsClick.emit();
        }
    };

    private removeFile = (event: NativeEvent, removedFile: File) => {
        event.preventDefault();
        this.joyDropzoneRemoveFile.emit(removedFile);
        this.files = this.files.filter((file) => file !== removedFile);

        /**
         * this.filter filter does not actually update the input Files, so we need to reconstruct its value
         */
        let actualInputFileList = new DataTransfer();

        this.files.forEach((file) => {
            actualInputFileList.items.add(file);
        });
        this.input.files = actualInputFileList.files;

        this.clearErrorStatus();
    };
}
